import React from 'react';

import AvatarSkeleton from '@atlaskit/avatar/skeleton';
import { cssMap } from '@atlaskit/css';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { Card } from '../../../common/ui/Card/Card';
import { CardAvatarFrame } from '../../../common/ui/Card/CardAvatarFrame';
import { CardCover } from '../../../common/ui/Card/CardCover';
import { CardFooter } from '../../../common/ui/Card/CardFooter';

const styles = cssMap({
	loadingContent: {
		gridArea: 'content',
		alignSelf: 'start',
		minWidth: 0,
	},
	titleLine: {
		display: 'flex',
		alignItems: 'center',
		height: '24px',
	},
	textLine: {
		display: 'flex',
		alignItems: 'center',
		height: '16px',
	},
	root: {
		boxSizing: 'border-box',
		height: '100%',
		width: '100%',
		overflow: 'hidden',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: 'auto auto auto',
		gridTemplateAreas: '"cover" "content" "metadata"',
		rowGap: token('space.150'),
	},
	cover: {
		position: 'relative',
		gridArea: 'cover',
		width: '100%',
		height: '56px',
	},
	coverBackground: {
		position: 'absolute',
		insetBlockStart: token('space.0'),
		insetBlockEnd: token('space.200'),
		insetInlineStart: token('space.0'),
		insetInlineEnd: token('space.0'),
	},
	coverForeground: {
		position: 'relative',
		zIndex: 1,
		boxSizing: 'border-box',
		display: 'grid',
		width: '100%',
		height: '100%',
		gridTemplateColumns: 'auto minmax(0, 1fr)',
		alignContent: 'center',
		alignItems: 'center',
		paddingTop: token('space.250'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
	},
	content: {
		gridArea: 'content',
		minWidth: 0,
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
	},
	metadata: {
		gridArea: 'metadata',
		minWidth: 0,
		marginTop: 'auto',
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

export type AgentCardSkeletonProps = {
	testId?: string;
};

const LoadingAvatar = (): React.JSX.Element => (
	<CardAvatarFrame>
		<AvatarSkeleton size="xlarge" appearance="hexagon" color={token('color.text')} />
	</CardAvatarFrame>
);

export const AgentCardSkeleton = ({ testId }: AgentCardSkeletonProps): React.JSX.Element => {
	if (expVal('jira_browse_agent_modal_update', 'isEnabled', false)) {
		return (
			<Card aria-hidden="true" hasCutout isDisabled testId={testId}>
				<CardCover
					name=""
					avatar={LoadingAvatar}
					coverStyle="plain"
					testId={testId ? `${testId}--cover` : undefined}
				/>
				<Stack
					xcss={styles.loadingContent}
					space="space.150"
					testId={testId ? `${testId}--content` : undefined}
				>
					<Stack>
						<Box xcss={styles.titleLine}>
							<Skeleton width="70%" height={16} />
						</Box>
						<Box xcss={styles.textLine}>
							<Skeleton width="45%" height={12} />
						</Box>
					</Stack>
					<Stack>
						<Box xcss={styles.textLine}>
							<Skeleton width="100%" height={12} />
						</Box>
						<Box xcss={styles.textLine}>
							<Skeleton width="80%" height={12} />
						</Box>
					</Stack>
				</Stack>
				<CardFooter testId={testId ? `${testId}--footer` : undefined}>
					<Skeleton width={24} height={24} borderRadius={token('radius.medium')} />
					<Skeleton width={24} height={24} borderRadius={token('radius.medium')} />
				</CardFooter>
			</Card>
		);
	}

	return (
		<Box xcss={styles.root} aria-hidden="true" testId={testId}>
			<Box xcss={styles.cover} testId={testId ? `${testId}--cover` : undefined}>
				<Box xcss={styles.coverBackground}>
					<Skeleton width="100%" height="100%" borderRadius={0} isShimmering />
				</Box>
				<Box xcss={styles.coverForeground}>
					<Skeleton width={32} height={32} isShimmering />
				</Box>
			</Box>

			<Box xcss={styles.content} testId={testId ? `${testId}--content` : undefined}>
				<Stack space="space.075">
					<Inline space="space.075" alignBlock="center">
						<Skeleton width={112} height={20} isShimmering />
						<Skeleton width={16} height={16} isShimmering />
					</Inline>
					<Stack space="space.025">
						<Skeleton width="100%" height={16} isShimmering />
					</Stack>
				</Stack>
			</Box>

			<Box xcss={styles.metadata} testId={testId ? `${testId}--metadata` : undefined}>
				<Inline space="space.075" alignBlock="center">
					<Skeleton width={72} height={16} isShimmering />
					<Skeleton width={16} height={16} isShimmering />
					<Skeleton width={32} height={16} isShimmering />
				</Inline>
			</Box>
		</Box>
	);
};
